// QR Scanner (phone-first): scan or type any Identity & Passes number - Duty Pass, Vehicle Pass, Pandit Seva Card or
// Children Tag - and see the full details with a clear VALID / EXPIRED / NOT YET VALID banner. A pre-printed children
// tag that is not given to a child yet offers "Register Child".
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { userPages } from "../lib/pages";
import { ID_KINDS, VALIDITY, dmy, recName, printIdentity } from "../lib/identity";
import { QrScanner } from "../components/QrScanButton";
import { useIdTemple } from "../components/IdentityKit";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Camera, Keyboard, ScanLine, ShieldCheck, ShieldAlert, ShieldX, Printer, UserPlus, Info, Phone } from "lucide-react";
import { toast } from "sonner";

const FIELD_VALUE = { department_id: "department_name", location_id: "location_name" };
const TINT = {
  VALID: ["from-green-600 to-emerald-600", ShieldCheck], EXPIRED: ["from-red-600 to-rose-600", ShieldX], REJECTED: ["from-red-600 to-rose-600", ShieldX],
  NOT_YET_VALID: ["from-sky-600 to-blue-600", ShieldAlert], PENDING: ["from-amber-500 to-orange-500", ShieldAlert],
};

export default function IdentityScan() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const pages = userPages(user);
  const { temples } = useIdTemple(user);
  const [mode, setMode] = useState("idle"); // idle | scan | manual
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [ranges, setRanges] = useState([]);

  useEffect(() => {
    if (pages.includes("ID_CHILDREN") || pages.includes("ID_MASTERS") || user.role === "SUPER_ADMIN")
      api.get("/identity/tag-ranges").then((r) => setRanges(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const check = async (raw) => {
    const v = String(raw ?? code).trim();
    if (!v) return;
    setBusy(true); setMode("idle");
    try {
      const r = await api.get("/identity/verify", { params: { code: v } });
      if (r.data.unassigned) { setRes(r.data); toast.success("Valid QR code! Fill in child details."); return; }
      const full = await api.get(`/identity/${r.data.kind}/${r.data.record.id}`);
      setRes({ ...r.data, record: full.data });
    } catch (e) { toast.error(fmtErr(e)); setRes(null); } finally { setBusy(false); }
  };
  const reset = () => { setRes(null); setCode(""); setMode("scan"); };

  const r = res?.record;
  const k = res && ID_KINDS[res.kind];
  const temple = r && temples.find((t) => t.temple_id === r.temple_id);
  const [grad, Icon] = TINT[r?.validity] || TINT.PENDING;
  const details = r ? k.fields.filter((f) => f.key !== "tag_serial").map((f) => [f.label.replace(/ \(.*\)$/, ""), r[FIELD_VALUE[f.key] || f.key]]).filter(([, v]) => v !== undefined && v !== null && v !== "") : [];

  return (
    <div className="max-w-lg mx-auto" data-testid="id-scan-page">
      <h1 className="font-heading text-3xl font-bold text-maroon-900">QR Scanner</h1>
      <p className="text-slate-500 mb-4">Scan a Duty Pass, Vehicle Pass, Seva Card or Children Tag to see its details.</p>

      {!res && (
        <>
          <div className="space-y-3 mb-4">
            <button onClick={() => setMode("scan")} className="w-full h-14 rounded-2xl bg-white border border-[#EADFD2] shadow-sm flex items-center justify-center gap-2 font-semibold text-slate-800 active:scale-[0.99]" data-testid="scan-btn-scan"><Camera className="w-5 h-5" /> Scan QR</button>
            <button onClick={() => setMode("manual")} className="w-full h-14 rounded-2xl bg-white border border-[#EADFD2] shadow-sm flex items-center justify-center gap-2 font-semibold text-slate-800 active:scale-[0.99]" data-testid="scan-btn-manual"><Keyboard className="w-5 h-5" /> Manual Entry</button>
          </div>
          <div className="rounded-3xl bg-white border border-[#EADFD2] shadow-sm p-5 text-center">
            {mode === "scan" ? (
              <>
                <QrScanner className="rounded-2xl min-h-[300px]" onScan={(t) => { setCode(t); check(t); }} onError={() => setMode("manual")} />
                <p className="text-sm text-slate-500 mt-3">Point the camera at the QR code. It scans automatically.</p>
                <Button variant="outline" className="mt-2" onClick={() => setMode("idle")}>Stop</Button>
              </>
            ) : mode === "manual" ? (
              <div className="text-left">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Pass number or tag serial</p>
                <Input autoFocus value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && check()} placeholder="DTY-T001-000001 or ssd0000001" className="h-12 font-mono text-base" data-testid="scan-input" />
                <Button onClick={() => check()} disabled={busy || !code.trim()} className="w-full h-12 mt-3 bg-[#0E7490] hover:bg-[#0B5E75] text-white text-base" data-testid="scan-check">Check</Button>
              </div>
            ) : (
              <>
                <div className="mx-auto w-20 h-20 rounded-full bg-sky-100 flex items-center justify-center"><ScanLine className="w-9 h-9 text-sky-600" /></div>
                <p className="font-bold text-xl text-slate-800 mt-4">{busy ? "Checking…" : "Ready to Scan"}</p>
                <p className="text-slate-500">Tap below to start QR code scanning</p>
                <Button onClick={() => setMode("scan")} className="mt-4 h-12 px-8 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-base" data-testid="scan-start"><Camera className="w-5 h-5 mr-2" /> Start Scanner</Button>
              </>
            )}
          </div>
          {ranges.length > 0 && (
            <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sky-800">
              <p className="flex items-center gap-2 font-semibold"><Info className="w-5 h-5" /> Valid Children Tag Ranges</p>
              {ranges.map((x) => <p key={x.id} className="font-mono font-bold mt-1 text-sm">{x.first_tag} → {x.last_tag} <span className="font-sans font-normal text-sky-600">· {(x.capacity - x.used).toLocaleString("en-IN")} available</span></p>)}
            </div>
          )}
        </>
      )}

      {res?.unassigned && (
        <div className="rounded-3xl bg-white border border-[#EADFD2] shadow-sm p-5" data-testid="scan-unassigned">
          <p className="font-bold text-lg text-slate-800">Children Tag not yet given</p>
          <p className="text-slate-500">Tag <span className="font-mono font-bold text-sky-700">{res.tag_serial}</span> is a valid tag of this temple and is free.</p>
          {pages.includes("ID_CHILDREN") && (
            <Button onClick={() => navigate(`/identity/assign-tag?tag=${encodeURIComponent(res.tag_serial)}`)} className="w-full h-12 mt-4 bg-[#E8590C] hover:bg-[#C2410C] text-white text-base" data-testid="scan-register-child"><UserPlus className="w-5 h-5 mr-2" /> Register Child</Button>
          )}
          <Button variant="outline" onClick={reset} className="w-full h-12 mt-2"><ScanLine className="w-5 h-5 mr-2" /> Scan another</Button>
        </div>
      )}

      {r && (
        <div className="space-y-3" data-testid="scan-result">
          <div className={`rounded-3xl bg-gradient-to-r ${grad} text-white p-5 text-center shadow-lg`}>
            <Icon className="w-12 h-12 mx-auto" />
            <p className="text-2xl font-extrabold mt-1" data-testid="scan-validity">{(VALIDITY[r.validity] || [r.validity])[0].toUpperCase()}</p>
            <p className="opacity-90">{k.label} · {dmy(r.valid_from)} to {dmy(r.valid_to)}</p>
            {r.remarks && <p className="mt-1 text-sm bg-white/20 rounded-lg px-2 py-1">{r.remarks}</p>}
          </div>
          <div className="rounded-3xl bg-white border border-[#EADFD2] shadow-sm p-5">
            <div className="flex gap-4 items-start">
              {k.photo && (r.photo ? <img src={r.photo} alt="" className="w-28 h-32 rounded-2xl object-cover border" /> : <div className="w-28 h-32 rounded-2xl bg-slate-100 flex items-center justify-center text-xs text-slate-400">No photo</div>)}
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-[#C2410C]">{k.label}</p>
                <p className="text-xl font-extrabold text-maroon-900 break-words">{recName(r)}</p>
                {r.vehicle_number && <p className="font-mono text-2xl font-extrabold">{r.vehicle_number}</p>}
                <p className="font-mono text-sm text-slate-600">{res.kind === "children" ? String(r.tag_serial).toUpperCase() : r.pass_no}</p>
                {r.mobile && <a href={`tel:${r.mobile}`} className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-xl bg-green-600 text-white font-semibold text-sm"><Phone className="w-4 h-4" /> {r.mobile}</a>}
              </div>
            </div>
            <table className="w-full text-sm mt-4"><tbody>
              {details.map(([l, v]) => (
                <tr key={l} className="border-t border-[#F1EBE3]"><td className="py-2 pr-3 text-slate-500 w-36">{l}</td><td className="py-2 font-semibold text-slate-800 break-words">{String(v)}</td></tr>
              ))}
              <tr className="border-t border-[#F1EBE3]"><td className="py-2 pr-3 text-slate-500">Status</td><td className="py-2 font-semibold">{r.status}</td></tr>
              <tr className="border-t border-[#F1EBE3]"><td className="py-2 pr-3 text-slate-500">Registered</td><td className="py-2">{dmy(r.created_at)} · {r.created_by_name}</td></tr>
              {r.approved_by && <tr className="border-t border-[#F1EBE3]"><td className="py-2 pr-3 text-slate-500">Approved by</td><td className="py-2">{r.approved_by}</td></tr>}
            </tbody></table>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={reset} className="h-12 bg-sky-500 hover:bg-sky-600 text-white text-base" data-testid="scan-again"><ScanLine className="w-5 h-5 mr-2" /> Scan next</Button>
            <Button variant="outline" onClick={() => printIdentity(res.kind, r, temple)} disabled={["PENDING", "REJECTED"].includes(r.status)} className="h-12 text-base"><Printer className="w-5 h-5 mr-2" /> Print</Button>
          </div>
        </div>
      )}
    </div>
  );
}
